import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useOrgFeatureFlags } from '../../hooks/useOrgFeatureFlags';
import { getActingOrganizationId } from '../../utils/actingOrganization';
import { getAuthHeaders, getImpersonationIdentityKey, makeApiCall } from '../../utils/apiConfig';
import type { PlanRequirementsPayload } from './planSurface';

export const PLAN_REQUIREMENTS_QUERY_KEY = ['billing-plan-requirements'] as const;

/**
 * Cheapest plan per feature for the acting store. The request is made only
 * while `show_locked_plan_features` is on and someone is signed in, so the
 * flag-off shell never calls it. The app's QueryClient outlives a sign-out,
 * so the key names the account, the store it is acting for, and any View-as.
 * A failed read fails open in `resolvePlanSurface`.
 */
export function usePlanRequirements() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid ?? null;
  const { isEnabled, isLoading: flagsLoading } = useOrgFeatureFlags();
  const showLocked = !flagsLoading && isEnabled('show_locked_plan_features');
  const query = useQuery({
    queryKey: [...PLAN_REQUIREMENTS_QUERY_KEY, uid, getActingOrganizationId(uid), getImpersonationIdentityKey()],
    enabled: showLocked && Boolean(uid),
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<PlanRequirementsPayload> => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/billing/plan-requirements', { headers });
      if (!response.ok) throw new Error('plan requirements unavailable');
      const body = await response.json();
      if (!body?.data?.features || !body?.data?.ranks) throw new Error('plan requirements unavailable');
      return body.data as PlanRequirementsPayload;
    },
  });

  return {
    flagsLoading,
    showLocked,
    requirements: query.data ?? null,
    // A disabled query (no signed-in user) is pending in react-query v5 and
    // has no data, so a sign-out holds the lock instead of reusing the last
    // account's plan.
    requirementsPending: showLocked && query.isPending,
    requirementsFailed: query.isError,
  };
}
